import { useQuery } from "@tanstack/react-query"
import { buildUrl } from "@/SX/api"

import useChainProviderHook from "@/hooks/useChainProvider"
import { SX_CONFIG } from "../constants"
import { getSportName } from "../utils/transformSxMarkets"

interface SXApiResponse {
  data: {
    fixture: {
      volume: number
      id: string
      teamOne: string
      teamTwo: string
      gameTime: string
      status: number
      sportId: number
      sportsName: string
      leagueId: number
      leagueLabel: string
      betGroups: string[]
    }
    orders: any[]
  }
}

export function calculateOdds(percentageOddsRaw: number, decimal = 10) {
  const scale = 10 ** 20
  const percentageOdds = parseFloat((percentageOddsRaw / scale).toFixed(6))

  if (isNaN(percentageOdds) || percentageOdds <= 0 || percentageOdds >= 1) {
    throw new Error(
      "Invalid percentageOdds value. It should be between 0 and 1 (exclusive)."
    )
  }

  const offeredOdds = parseFloat(
    (scale / (scale - percentageOdds * scale)).toFixed(decimal)
  )

  return offeredOdds
}

const createMarketData = (
  marketType: string,
  market: any,
  isOrderBook = false
) => {
  try {
    if (isOrderBook) {
      // Handle orderBook data structure
      const one = market.one?.[0]
      const two = market.two?.[0]

      if (!one || !two) {
        return null
      }

      // if (one.percentageOddsRaw === null && two.percentageOddsRaw === null) {
      //   return null
      // }

      return {
        marketType,
        outcomeOneName: market.outcomeOneName,
        outcomeTwoName: market.outcomeTwoName,
        providerMarketType: market.providerMarketType,
        market: {
          marketHash: market.marketHash,
          baseToken: one.baseToken,
          bestOddsOutcomeOne: one.percentageOddsRaw,
          bestOddsOutcomeTwo: two.percentageOddsRaw,
          outcomeOneOdds: one.percentageOddsRaw
            ? calculateOdds(one.percentageOddsRaw)
            : null,
          outcomeTwoOdds: two.percentageOddsRaw
            ? calculateOdds(two.percentageOddsRaw)
            : null,
        },
      }
    } else {
      // Handle altlines data structure
      if (!market.market) {
        return null
      }

      // if (
      //   market.market.bestOddsOutcomeOne === null &&
      //   market.market.bestOddsOutcomeTwo === null
      // ) {
      //   return null
      // }
      return {
        marketType: market.marketType,
        outcomeOneName: market.outcomeOneName,
        outcomeTwoName: market.outcomeTwoName,
        providerMarketType: "altlines",
        market: {
          marketHash: market.market.marketHash,
          baseToken: market.market.baseToken,
          bestOddsOutcomeOne: market.market.bestOddsOutcomeOne,
          bestOddsOutcomeTwo: market.market.bestOddsOutcomeTwo,
          outcomeOneOdds: market.market.bestOddsOutcomeTwo
            ? calculateOdds(market.market.bestOddsOutcomeTwo)
            : null,
          outcomeTwoOdds: market.market.bestOddsOutcomeOne
            ? calculateOdds(market.market.bestOddsOutcomeOne)
            : null,
        },
      }
    }
  } catch (error) {
    console.warn(`⚠️ Error processing market ${marketType}:`, error.message)
    return null
  }
}

const fetchMergedMarketData = async (
  eventId: string,
  betGroup: string,
  chainId: number
): Promise<SXApiResponse> => {
  try {
    const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN

    // First, fetch the initial bet group data
    const response = await fetch(
      buildUrl(`/summary/event/${eventId}/${betGroup}/${TOKEN}`, {
        chainId: chainId.toString(),
      })
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const apiData = await response.json()

    const orderBook = apiData.data.orderBook
    if (Object.keys(orderBook).length === 0) {
      return {
        data: {
          fixture: {
            ...apiData.data.fixture,
            betGroups: [],
          },
          orders: [],
        },
      }
    }
    const betGroups = apiData.data.fixture.betGroups

    const gameLabel = `${apiData.data.fixture.teamOne} - ${apiData.data.fixture.teamTwo}`

    // Create a copy of betGroups to preserve the original
    const originalBetGroups = [...betGroups]

    // Remove the current betGroup from the array
    const index = betGroups.indexOf(betGroup)
    if (index !== -1) {
      betGroups.splice(index, 1)
    }

    // Start with the initial orderBook
    let mergedOrderBook = { ...apiData.data.orderBook }

    // Fetch and merge additional bet groups
    for (const remainingBetGroup of betGroups) {
      const betGroupResponse = await fetch(
        buildUrl(`/summary/event/${eventId}/${remainingBetGroup}/${TOKEN}`, {
          chainId: chainId.toString(),
        })
      )
      if (!betGroupResponse.ok) {
        throw new Error(`HTTP error! status: ${betGroupResponse.status}`)
      }

      const betGroupData = await betGroupResponse.json()

      // Merge the orderBook data
      const additionalOrderBook = betGroupData.data.orderBook
      mergedOrderBook = { ...mergedOrderBook, ...additionalOrderBook }
    }

    const orders: any[] = []
    if (mergedOrderBook) {
      Object.entries(mergedOrderBook).forEach(
        ([marketType, orderBookMarket]) => {
          const data = createMarketData(marketType, orderBookMarket, true)
          if (data) {
            orders.push(data)
          }
        }
      )
    } else {
      console.log("⚠️ No orderBook data found in mergedData")
    }

    try {
      const altlinesResponse = await fetch(
        buildUrl(`/summary/altlines/${eventId}/game-lines/${TOKEN}`, {
          chainId: chainId.toString(),
        })
      )

      if (altlinesResponse.ok) {
        const altlinesData = await altlinesResponse.json()

        // Process altlines data
        if (altlinesData.data) {
          Object.values(altlinesData.data)
            .flat()
            .map((market: any) =>
              createMarketData(market.marketType, market, false)
            )
            .filter(Boolean)
            .forEach(data => orders.push(data))
        }
      } else {
        console.warn(
          `⚠️ Altlines API returned status: ${altlinesResponse.status}`
        )
      }
    } catch (altlinesError) {
      console.warn("⚠️ Error fetching altlines data:", altlinesError.message)
    }

    const uniqueOrders = orders.filter((order, index, self) => {
      const firstIndex = self.findIndex(
        o =>
          o.marketType === order.marketType &&
          o.market.marketHash === order.market.marketHash
      )
      return firstIndex === index
    })

    return {
      data: {
        fixture: {
          ...apiData.data.fixture,
          betGroups: originalBetGroups,
          gameLabel,
          sportsName: getSportName(apiData.data.fixture.sportId),
        },
        orders: uniqueOrders,
      },
    }
  } catch (error) {
    console.error("Error calling SX.Bet API:", error)
    throw error
  }
}

const useSXMarketData = (eventId: string, betGroup: string = "game-lines") => {
  const { isSXChain, chainId } = useChainProviderHook()

  const mergedMarketDataQuery = useQuery({
    queryKey: ["mergedMarketData", eventId, betGroup],
    queryFn: () => fetchMergedMarketData(eventId, betGroup, chainId),
    enabled: !!eventId && isSXChain,
  })

  return {
    status: "done",
    data: mergedMarketDataQuery.data?.data,
    isLoading: mergedMarketDataQuery.isLoading,
    isError: mergedMarketDataQuery.isError,
    error: mergedMarketDataQuery.error,
    refetch: mergedMarketDataQuery.refetch,
  }
}

export default useSXMarketData
